(function () {
  window.visibilityService = {
    /**
     * Get current visibility settings from server
     * @returns {Promise} Promise that resolves with visibility settings
     */
    getSettings() {
      return axios.get('/api/visibility/get.php');
    },

    /**
     * Save visibility settings to server
     * @param {Object} data - Settings to save (battleVisibility, packVisibility)
     * @returns {Promise} Promise that resolves when settings are saved
     */
    saveSettings(data) {
      return axios.post('/api/visibility/save.php', data);
    },

    /**
     * Reset visibility settings to defaults
     * @returns {Promise} Promise that resolves when settings are reset
     */
    resetSettings() {
      return axios.post('/api/visibility/reset.php', {});
    },

    /**
     * Get streamer's overlay visibility settings
     * @param {string} channelId - Streamer's Twitch ID
     * @returns {Promise} Promise that resolves with streamer's visibility settings
     */
    getStreamerSetting(channelId) {
      return axios.get(`/api/visibility/stream/get-setting.php?channelId=${channelId}`);
    },

    /**
     * Set streamer's overlay visibility settings (streamer only)
     * @param {Object} data - { overlay_visibility: 'all' | 'own' }
     * @returns {Promise} Promise that resolves when settings are saved
     */
    setStreamerSetting(data) {
      return axios.post('/api/visibility/stream/set.php', data);
    },

    /**
     * Get pending visibility change requests for streamer
     * @returns {Promise} Promise that resolves with pending requests
     */
    getStreamerRequests() {
      return axios.get('/api/visibility/stream/requests.php');
    },

    /**
     * Approve or deny a visibility change request
     * @param {Object} data - { requestId, action: 'approve'|'deny', response?: string }
     * @returns {Promise} Promise that resolves when request is processed
     */
    respondToRequest(data) {
      return axios.post('/api/visibility/stream/approve-request.php', data);
    },

    /**
     * Request streamer to change visibility settings
     * @param {Object} data - { channelId, requestedSetting: 'all'|'own', role: 'user'|'mod' }
     * @returns {Promise} Promise that resolves when request is submitted
     */
    requestVisibilityChange(data) {
      return axios.post('/api/visibility/request-change.php', data);
    },

    /**
     * Check if a battle should be shown to the current user
     * Respects both user's personal settings and streamer's overlay settings
     * @param {Object} battle - Battle object with user1, user2, user1TwitchId, user2TwitchId
     * @param {string} currentUserId - Current user's Twitch ID
     * @param {Object} userSettings - Current user's visibility settings
     * @param {Object} streamerSettings - Streamer's overlay visibility settings
     * @param {string} context - 'dashboard' or 'stream' (OBS overlay)
     * @returns {boolean} Whether battle should be visible
     */
    shouldShowBattle(battle, currentUserId, userSettings, streamerSettings, context = 'dashboard') {
      if (!battle) {
        return false;
      }

      // For dashboard view: only respect user's personal settings
      if (context === 'dashboard') {
        if (!userSettings) {
          return true; // Default to showing if no settings
        }

        if (userSettings.battleVisibility === 'none') {
          return false;
        }

        if (userSettings.battleVisibility === 'own') {
          // Check if current user is involved in the battle
          const user1Id = battle.user1?.twitchId || battle.user1TwitchId;
          const user2Id = battle.user2?.twitchId || battle.user2TwitchId;
          return currentUserId === user1Id || currentUserId === user2Id;
        }

        // 'all' - show everything
        return true;
      }

      // For stream/OBS overlay view: respect streamer's overlay setting
      if (context === 'stream') {
        if (!streamerSettings) {
          return true; // Default to showing if no settings
        }

        if (streamerSettings.overlay_visibility === 'own') {
          // Only show battles where the streamer is involved
          // Need to know the streamer's ID to check this
          const user1Id = battle.user1?.twitchId || battle.user1TwitchId;
          const user2Id = battle.user2?.twitchId || battle.user2TwitchId;
          // This would need the streamer ID passed separately
          // For now, return true as streamer usually doesn't restrict their own content
          return true;
        }

        // 'all' - show everything
        return true;
      }

      // Unknown context defaults to showing
      return true;
    },

    /**
     * Check if a pack opening should be shown to the current user
     * Uses local cache of settings - call getSettings() first
     * @param {Object} packOpening - Pack opening object with user info
     * @param {string} currentUserId - Current user's ID
     * @param {Object} currentSettings - Current visibility settings
     * @returns {boolean} Whether pack opening should be visible
     */
    shouldShowPackOpening(packOpening, currentUserId, currentSettings) {
      // DEBUG LOG
      console.group('📦 VISIBILITY CHECK - PACK OPENING');
      console.log('Your Twitch ID:', currentUserId);
      console.log('Your Visibility Setting:', currentSettings?.packVisibility || 'none');
      console.log('Pack Owner Twitch ID:', packOpening?.twitchId || packOpening?.user?.twitchId);
      console.log('Full Pack Data:', packOpening);

      if (!currentSettings) {
        console.log('Result: ✅ SHOW (no settings, default to show)');
        console.groupEnd();
        return true;
      }

      if (currentSettings.packVisibility === 'none') {
        console.log('Result: ❌ HIDE (visibility set to none)');
        console.groupEnd();
        return false;
      }

      if (currentSettings.packVisibility === 'own') {
        // Check if current user opened the pack
        const packOwnerId = packOpening.twitchId || packOpening.user?.twitchId;
        const shouldShow = currentUserId === packOwnerId;
        console.log('Checking own pack:', {
          packOwnerId: packOwnerId,
          currentUserId: currentUserId,
          isMatch: shouldShow
        });
        console.log('Result:', shouldShow ? '✅ SHOW (own pack)' : '❌ HIDE (not own pack)');
        console.groupEnd();
        return shouldShow;
      }

      // 'all' - show everything
      console.log('Result: ✅ SHOW (visibility set to all)');
      console.groupEnd();
      return true;
    }
  };
})();
